/**
 * Markup léger des messages (inbox, notifications, rapports).
 *
 * Le backend n'écrit JAMAIS d'id brut (« res #4 ») dans un corps de message :
 * il émet un jeton porteur d'une icône ET d'un libellé de repli :
 *
 *   [[res:fer|Fer]]            -> icône ressource + « Fer »
 *   [[item:cristal_noir|Cristal noir]]
 *   [[img:buildings/qg|QG]]    -> image générique dans /images/<folder>/<code>.png
 *   [[planet:42|Xandar III]]   -> puce stylée (pas d'image), nom de planète
 *   [[fleet:7|1re Flotte]]     -> puce stylée, nom de flotte
 *
 * Le libellé après « | » garantit une dégradation propre : un affichage qui
 * ne passe pas par <MessageBody> peut appeler stripMessageMarkup() pour
 * récupérer un texte lisible (« Fer »).
 */

export type MessageSegmentKind = 'text' | 'res' | 'item' | 'img' | 'planet' | 'fleet';

export interface MessageSegment {
  kind: MessageSegmentKind;
  /** Pour 'text' : le texte. Pour les jetons : le libellé affiché. */
  text: string;
  /** Référence du jeton (code ressource/objet, "folder/code", ou id). */
  ref?: string;
  /** URL d'image résolue (undefined pour text / planet / fleet). */
  image?: string;
}

const TOKEN_RE = /\[\[(res|item|img|planet|fleet):([^|\]]+)\|([^\]]*)\]\]/g;

/** Résout l'URL d'image (miniature 64) d'un jeton, ou undefined. */
function resolveImage(kind: MessageSegmentKind, ref: string): string | undefined {
  switch (kind) {
    case 'res':
      return `/images/thumb/64/resources/${ref}.png`;
    case 'item':
      return `/images/thumb/64/items/${ref}.png`;
    case 'img':
      // ref = "folder/code"
      return `/images/thumb/64/${ref}.png`;
    default:
      return undefined;
  }
}

/** Découpe un corps de message en segments (texte + jetons). */
export function parseMessageMarkup(body: string | null | undefined): MessageSegment[] {
  if (!body) return [];
  const segments: MessageSegment[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(body)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ kind: 'text', text: body.slice(lastIndex, m.index) });
    }
    const kind = m[1] as MessageSegmentKind;
    const ref = m[2];
    const label = m[3];
    segments.push({ kind, text: label, ref, image: resolveImage(kind, ref) });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < body.length) {
    segments.push({ kind: 'text', text: body.slice(lastIndex) });
  }
  return segments;
}

/** Remplace les jetons par leur seul libellé (texte brut lisible). */
export function stripMessageMarkup(body: string | null | undefined): string {
  if (!body) return '';
  return body.replace(TOKEN_RE, (_full, _kind, _ref, label) => label);
}

/** True si le corps contient au moins un jeton markup. */
export function hasMessageMarkup(body: string | null | undefined): boolean {
  if (!body) return false;
  TOKEN_RE.lastIndex = 0;
  return TOKEN_RE.test(body);
}
